import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';

const CookieConsent = () => {
  const { i18n } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = (accepted) => {
    setIsExiting(true);
    setTimeout(() => {
      localStorage.setItem('cookieConsent', accepted ? 'accepted' : 'declined');
      localStorage.setItem('cookieConsentDate', new Date().toISOString());
      setShowBanner(false);
    }, 300);
  };

  const texts = {
    en: {
      title: 'We use cookies',
      description: 'We use cookies to improve your experience on our website. By continuing to browse, you agree to our use of cookies.',
      accept: 'Accept All',
      decline: 'Decline',
      learnMore: 'Learn more about our cookie policy'
    },
    sv: {
      title: 'Vi använder cookies',
      description: 'Vi använder cookies för att förbättra din upplevelse på vår webbplats. Genom att fortsätta surfa godkänner du vår användning av cookies.',
      accept: 'Acceptera alla',
      decline: 'Avböja',
      learnMore: 'Läs mer om vår cookiepolicy'
    },
    ar: {
      title: 'نستخدم ملفات تعريف الارتباط',
      description: 'نستخدم ملفات تعريف الارتباط لتحسين تجربتك على موقعنا. بمتابعة التصفح، فإنك توافق على استخدامنا لملفات تعريف الارتباط.',
      accept: 'قبول الكل',
      decline: 'رفض',
      learnMore: 'اقرأ المزيد عن سياسة الكوكيز'
    }
  };

  const lang = texts[i18n.language] || texts.en;
  const isRTL = i18n.language === 'ar';

  if (!showBanner) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 transition-all duration-300 ease-out ${isExiting ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100 animate-slide-up'}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto bg-white dark:bg-[#18181b] rounded-2xl shadow-2xl border border-slate-200 dark:border-[#27272a] overflow-hidden">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="hidden sm:flex w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 items-center justify-center flex-shrink-0">
              <Cookie className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-900 dark:text-[#fafafa] mb-1 flex items-center gap-2">
                <Cookie className="w-5 h-5 text-emerald-600 sm:hidden" />
                {lang.title}
              </h3>
              <p className="text-sm text-slate-600 dark:text-[#d4d4d8] leading-relaxed">
                {lang.description}{' '}
                <Link 
                  to="/cookies" 
                  className="text-emerald-600 hover:text-emerald-700 underline font-medium"
                  data-testid="cookie-learn-more-link"
                >
                  {lang.learnMore}
                </Link>
              </p>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
                className="flex-1 sm:flex-none h-10 px-4 rounded-xl border-slate-300 text-slate-600 hover:bg-slate-100"
                data-testid="cookie-decline-btn"
              >
                {lang.decline}
              </Button>
              <Button
                onClick={() => handleClose(true)}
                className="flex-1 sm:flex-none h-10 px-6 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium"
                data-testid="cookie-accept-btn"
              >
                {lang.accept}
              </Button>
            </div>
            <button
              onClick={() => handleClose(false)}
              className="absolute top-2 end-2 sm:hidden p-2 text-slate-400 hover:text-slate-600"
              aria-label="Close cookie banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
